import * as React from 'react';
import { FC } from 'react';
import { Alert, Form } from 'reactstrap';
import { SettingsItemContentProps } from '../SettingsItem';
import { IntegrationsSettingsData, IntegrationWebhookSettings } from '../../../models';
import { AutocompleteTextField, MultipleTextField, SwitchField, TextInputField } from '../../../components/Forms/fields';
import { Row } from '../../../components/Forms/table/Row';
import { Col } from '../../../components/Forms/table/Column';
import { useTranslations } from '../../../store/translations/hooks';
import { TK } from '../../../store/translations/translationKeys';

export interface IntegrationsSettingsProps extends SettingsItemContentProps {
    values: IntegrationsSettingsData,
    onChange: (settings: IntegrationsSettingsData, save?: boolean) => void,
}

export const WebhookSettings : FC<IntegrationsSettingsProps> = ({
    id,
    values,
    onChange,
}) => {
    const t = useTranslations();

    const webhookSettings: IntegrationWebhookSettings = values.webhook ?? {
        url: '',
        isActive: false,
        synchronous: false,
        events: [
            'client.created',
            'client.updated',
            'client.deleted',
            'appointment.created',
            'appointment.updated',
            'appointment.deleted',
            'professional.created',
            'professional.updated',
            'professional.deleted',
        ],
        headers: {},
    }
    
    return (
        <Form style={{display: 'flex', marginTop: 'var(--spacer-S)', flexDirection: 'column', gap: 'var(--spacer-M)'}}>
            <Alert color="secondary" isOpen>
                {t(TK.webhookSettingsDescription)}
            </Alert>
            
            <Row>
                <Col md={2}>
                    <SwitchField
                        id={`${id}-webhook-enable`}
                        label='Enable'
                        value={webhookSettings.isActive}
                        onChange={isActive => onChange({...values, webhook: {...webhookSettings, isActive}})} />
                </Col>
                <Col md={10}>
                    <TextInputField
                        id={`${id}-webhook-url`}
                        label='URL'
                        value={webhookSettings.url}
                        type="text"
                        onChange={url => onChange({...values, webhook: {...webhookSettings, url}})} />
                </Col>
            </Row>
            <Row>
                <Col md={2}>
                    <SwitchField
                        id={`${id}-webhook-synchronous`}
                        label='Synchronous'
                        value={webhookSettings.synchronous}
                        onChange={synchronous => onChange({...values, webhook: {...webhookSettings, synchronous}})} />
                </Col>
                <Col md={10}>
                    <TextInputField
                        id={`${id}-webhook-secret`}
                        label='Secret (Optional)'
                        value={webhookSettings.secret}
                        type="text"
                        onChange={secret => onChange({...values, webhook: {...webhookSettings, secret: secret.trim().length ? secret : undefined}})} />
                </Col>
            </Row>
            <AutocompleteTextField
                id={`${id}-webhook-events`}
                label='Events'
                multiSelect
                values={webhookSettings.events}
                options={[
                    'client.created',
                    'client.updated',
                    'client.deleted',
                    'appointment.created',
                    'appointment.updated',
                    'appointment.deleted',
                    'professional.created',
                    'professional.updated',
                    'professional.deleted',
                ].filter(e => !webhookSettings.events?.includes(e))}
                onValuesChanged={events => onChange({...values, webhook: {...webhookSettings, events}})} />
            <MultipleTextField
                id={`${id}-webhook-headers`}
                label='Headers'
                values={webhookSettings.headers ? Object.entries(webhookSettings.headers).map(([key, value]) => `${key}:${value}`) : []}
                onChange={headers => {
                    const parsedHeaders = headers.reduce<Record<string, string>>((acc, header) => {
                        const separatorIndex = header.indexOf(':');
                        if (separatorIndex <= 0) return acc;
                        const key = header.substring(0, separatorIndex).trim();
                        const value = header.substring(separatorIndex + 1).trim();
                        if (!key) return acc;
                        acc[key] = value;
                        return acc;
                    }, {});

                    onChange({
                        ...values,
                        webhook: {
                            ...webhookSettings,
                            headers: parsedHeaders,
                        },
                    });
                }} />
        </Form>
    )
}
